/**
 * Cover 3 Match Coverage Rules
 * Defines CB reaction patterns for Cover 3 Match coverage
 * Requirements: 7.1, 7.3
 * 
 * Note: Currently mirrors Cover 4 Match rules - will be customized independently
 */

import { CoverageDefinition, CoverageReaction, Position, DBLBReaction, FSReaction, CB2Reaction, DBLB2Reaction, SSReaction, LBReaction } from '../types';

/**
 * Starting position for CB1 in Cover 3 Match coverage
 */
const CB1_COVER3MATCH_START: Position = { x: 20, y: 65 };

/**
 * Starting position for DB/LB1 in Cover 3 Match coverage
 */
const DBLB1_COVER3MATCH_START: Position = { x: 30, y: 72 };

/**
 * Starting position for FS in Cover 3 Match coverage
 */
const FS_COVER3MATCH_START: Position = { x: 50, y: 50 };

/**
 * Starting position for CB2 in Cover 3 Match coverage
 */
const CB2_COVER3MATCH_START: Position = { x: 80, y: 65 };

/**
 * Starting position for DB/LB2 in Cover 3 Match coverage
 * Moved inside to split the center with LB (LB at x=45, so DB/LB2 at x=55)
 */
const DBLB2_COVER3MATCH_START: Position = { x: 55, y: 72 };

/**
 * Starting position for SS in Cover 3 Match coverage
 * Mirrored position of DB/LB1 on the right side
 */
const SS_COVER3MATCH_START: Position = { x: 70, y: 72 };

/**
 * Starting position for LB in Cover 3 Match coverage
 * Shifted left to balance with DB/LB2, even depth
 */
const LB_COVER3MATCH_START: Position = { x: 45, y: 72 };

/**
 * Cover 3 Match reaction rules for CB1
 * CB1 matches WR1 on routes 10 yards or longer (deepOut, deepIn, comeback, go, post, corner)
 * On short routes (slant, out): CB drops to deep third zone
 */
const cover3MatchReactions: CoverageReaction[] = [
  {
    // Slant: Under 10yds - CB drops to deep third zone (FS pace)
    routeTrigger: 'slant',
    wrPositionTrigger: { x: 20, y: 75 },
    cbResponse: [
      { x: 20, y: 50 },
      { x: 20, y: 35 },
    ],
    responseDelay: 0,
  },
  {
    // Out: Under 10yds - CB drops to deep third zone (FS pace)
    routeTrigger: 'out',
    wrPositionTrigger: { x: 20, y: 70 },
    cbResponse: [
      { x: 20, y: 50 },
      { x: 20, y: 35 },
    ],
    responseDelay: 0,
  },
  {
    // Deep Out: 10+ yds - CB matches WR downfield then to sideline
    routeTrigger: 'deepOut',
    wrPositionTrigger: { x: 20, y: 45 },
    cbResponse: [
      { x: 20, y: 47 },
      { x: 7, y: 45 },
    ],
    responseDelay: 0,
  },
  {
    // Deep In: 10+ yds - CB matches WR downfield then across the field
    routeTrigger: 'deepIn',
    wrPositionTrigger: { x: 20, y: 45 },
    cbResponse: [
      { x: 20, y: 47 },
      { x: 58, y: 45 },
    ],
    responseDelay: 0,
  },
  {
    // Comeback: 10+ yds - CB matches WR deep, closes when WR comes back
    routeTrigger: 'comeback',
    wrPositionTrigger: { x: 20, y: 50 },
    cbResponse: [
      { x: 20, y: 27 },
      { x: 17, y: 40 },
    ],
    responseDelay: 0,
  },
  {
    // Go: 10+ yds - CB turns and runs with WR, stays on hip
    routeTrigger: 'go',
    wrPositionTrigger: { x: 20, y: 70 },
    cbResponse: [
      { x: 20, y: 40 },
      { x: 20, y: 8 },
    ],
    responseDelay: 0,
  },
  {
    // Post: 10+ yds - CB matches WR, breaks inside with him
    routeTrigger: 'post',
    wrPositionTrigger: { x: 20, y: 55 },
    cbResponse: [
      { x: 20, y: 57 },
      { x: 48, y: 18 },
    ],
    responseDelay: 0,
  },
  {
    // Corner: CB matches WR, breaks outside with him
    routeTrigger: 'corner',
    wrPositionTrigger: { x: 20, y: 55 },
    cbResponse: [
      { x: 20, y: 57 },
      { x: 7, y: 18 },
    ],
    responseDelay: 0,
  },
  {
    // Drag: Shallow cross - CB drops to deep third zone (FS pace)
    routeTrigger: 'drag',
    wrPositionTrigger: { x: 20, y: 80 },
    cbResponse: [
      { x: 20, y: 50 },
      { x: 20, y: 35 },
    ],
    responseDelay: 0,
  },
  {
    // Hitch: 5-yard stop - CB drops to deep third zone (FS pace)
    routeTrigger: 'hitch',
    wrPositionTrigger: { x: 20, y: 70 },
    cbResponse: [
      { x: 20, y: 50 },
      { x: 20, y: 35 },
    ],
    responseDelay: 0,
  },
  {
    // Curl: 10-yard stop - CB matches WR, closes on the turn
    routeTrigger: 'curl',
    wrPositionTrigger: { x: 20, y: 55 },
    cbResponse: [
      { x: 20, y: 57 },
      { x: 22, y: 60 },
    ],
    responseDelay: 0,
  },
];

/**
 * DB/LB1 reaction rules for Cover 3 Match
 * DB/LB1 matches WR2 on vertical routes (go, post, corner, comeback, deepOut, deepIn) and short out
 * Lets WR2 go on 5yd or less inside routes (slant)
 */
const cover3MatchDBLB1Reactions: DBLBReaction[] = [
  {
    // Slant: 5yd inward route - let WR2 go, drop to zone
    routeTrigger: 'slant',
    response: [
      { x: 30, y: 65 },
      { x: 32, y: 55 },
      { x: 35, y: 45 },
    ],
  },
  {
    // Out: Short 5-yard out - match WR2 to the flat
    routeTrigger: 'out',
    response: [
      { x: 28, y: 70 },
      { x: 20, y: 70 },
      { x: 10, y: 70 },
    ],
  },
  {
    // Out by WR1: Short 5-yard out - match WR1 to the flat
    routeTrigger: 'out_wr1',
    response: [
      { x: 28, y: 65 },
      { x: 22, y: 68 },
      { x: 8, y: 68 },
    ],
  },
  {
    // Deep Out: Vertical route - match WR2 downfield then to sideline
    routeTrigger: 'deepOut',
    response: [
      { x: 30, y: 60 },
      { x: 30, y: 47 },
      { x: 15, y: 45 },
    ],
  },
  {
    // Deep In: Vertical route - match WR2 downfield then across
    routeTrigger: 'deepIn',
    response: [
      { x: 30, y: 60 },
      { x: 30, y: 47 },
      { x: 55, y: 45 },
    ],
  },
  {
    // Comeback: Vertical route - match WR2 deep, close when he comes back
    routeTrigger: 'comeback',
    response: [
      { x: 30, y: 55 },
      { x: 30, y: 35 },
      { x: 28, y: 42 },
    ],
  },
  {
    // Go: Vertical route - match WR2 deep
    routeTrigger: 'go',
    response: [
      { x: 30, y: 55 },
      { x: 30, y: 35 },
      { x: 30, y: 15 },
    ],
  },
  {
    // Post: Vertical route - match WR2, break inside with him
    routeTrigger: 'post',
    response: [
      { x: 30, y: 60 },
      { x: 30, y: 50 },
      { x: 48, y: 20 },
    ],
  },
  {
    // Corner: Vertical route - match WR2, break outside with him
    routeTrigger: 'corner',
    response: [
      { x: 30, y: 60 },
      { x: 30, y: 50 },
      { x: 12, y: 20 },
    ],
  },
  {
    // Drag: Shallow cross - match WR2 across
    routeTrigger: 'drag',
    response: [
      { x: 30, y: 68 },
      { x: 35, y: 65 },
      { x: 70, y: 60 },
    ],
  },
  {
    // Hitch: 5-yard stop - match WR2 to the flat
    routeTrigger: 'hitch',
    response: [
      { x: 28, y: 70 },
      { x: 30, y: 73 },
      { x: 32, y: 76 },
    ],
  },
  {
    // Curl: 10-yard stop - match WR2, close on turn
    routeTrigger: 'curl',
    response: [
      { x: 30, y: 60 },
      { x: 30, y: 50 },
      { x: 32, y: 55 },
    ],
  },
];

/**
 * FS reaction rules for Cover 3 Match
 * FS maintains deep center zone responsibility - drops straight back
 */
const cover3MatchFSReactions: FSReaction[] = [
  {
    routeTrigger: 'slant',
    response: [
      { x: 50, y: 35 },
      { x: 50, y: 20 },
    ],
  },
  {
    routeTrigger: 'out',
    response: [
      { x: 50, y: 35 },
      { x: 50, y: 20 },
    ],
  },
  {
    routeTrigger: 'deepOut',
    response: [
      { x: 50, y: 35 },
      { x: 50, y: 20 },
    ],
  },
  {
    routeTrigger: 'deepIn',
    response: [
      { x: 50, y: 35 },
      { x: 50, y: 20 },
    ],
  },
  {
    routeTrigger: 'comeback',
    response: [
      { x: 50, y: 35 },
      { x: 50, y: 20 },
    ],
  },
  {
    routeTrigger: 'go',
    response: [
      { x: 50, y: 35 },
      { x: 50, y: 20 },
    ],
  },
  {
    routeTrigger: 'post',
    response: [
      { x: 50, y: 35 },
      { x: 50, y: 20 },
    ],
  },
  {
    routeTrigger: 'corner',
    response: [
      { x: 50, y: 35 },
      { x: 50, y: 20 },
    ],
  },
  {
    routeTrigger: 'drag',
    response: [
      { x: 50, y: 35 },
      { x: 50, y: 20 },
    ],
  },
  {
    routeTrigger: 'hitch',
    response: [
      { x: 50, y: 35 },
      { x: 50, y: 20 },
    ],
  },
  {
    routeTrigger: 'curl',
    response: [
      { x: 50, y: 35 },
      { x: 50, y: 20 },
    ],
  },
];

/**
 * CB2 reaction rules for Cover 3 Match
 * CB2 matches WR4 on routes 10 yards or longer (deepOut, deepIn, comeback, go, post, corner)
 * On short routes (slant, out): CB drops to deep third zone
 */
const cover3MatchCB2Reactions: CB2Reaction[] = [
  {
    // Slant: Under 10yds - CB drops to deep third zone (FS pace)
    routeTrigger: 'slant',
    wrPositionTrigger: { x: 80, y: 75 },
    cbResponse: [
      { x: 80, y: 50 },
      { x: 80, y: 35 },
    ],
    responseDelay: 0,
  },
  {
    // Out: Under 10yds - CB drops to deep third zone (FS pace)
    routeTrigger: 'out',
    wrPositionTrigger: { x: 80, y: 70 },
    cbResponse: [
      { x: 80, y: 50 },
      { x: 80, y: 35 },
    ],
    responseDelay: 0,
  },
  {
    // Deep Out: 10+ yds - CB matches WR downfield then to sideline
    routeTrigger: 'deepOut',
    wrPositionTrigger: { x: 80, y: 45 },
    cbResponse: [
      { x: 80, y: 47 },
      { x: 93, y: 45 },
    ],
    responseDelay: 0,
  },
  {
    // Deep In: 10+ yds - CB matches WR downfield then across the field
    routeTrigger: 'deepIn',
    wrPositionTrigger: { x: 80, y: 45 },
    cbResponse: [
      { x: 80, y: 47 },
      { x: 42, y: 45 },
    ],
    responseDelay: 0,
  },
  {
    // Comeback: 10+ yds - CB matches WR deep, closes when WR comes back
    routeTrigger: 'comeback',
    wrPositionTrigger: { x: 80, y: 50 },
    cbResponse: [
      { x: 80, y: 27 },
      { x: 83, y: 40 },
    ],
    responseDelay: 0,
  },
  {
    // Go: 10+ yds - CB turns and runs with WR, stays on hip
    routeTrigger: 'go',
    wrPositionTrigger: { x: 80, y: 70 },
    cbResponse: [
      { x: 80, y: 40 },
      { x: 80, y: 8 },
    ],
    responseDelay: 0,
  },
  {
    // Post: 10+ yds - CB matches WR, breaks inside with him
    routeTrigger: 'post',
    wrPositionTrigger: { x: 80, y: 55 },
    cbResponse: [
      { x: 80, y: 57 },
      { x: 52, y: 18 },
    ],
    responseDelay: 0,
  },
  {
    // Corner: 10+ yds - CB matches WR, breaks outside with him
    routeTrigger: 'corner',
    wrPositionTrigger: { x: 80, y: 55 },
    cbResponse: [
      { x: 80, y: 57 },
      { x: 93, y: 18 },
    ],
    responseDelay: 0,
  },
  {
    // Drag: Shallow cross - CB drops to deep third zone (FS pace)
    routeTrigger: 'drag',
    wrPositionTrigger: { x: 80, y: 80 },
    cbResponse: [
      { x: 80, y: 50 },
      { x: 80, y: 35 },
    ],
    responseDelay: 0,
  },
  {
    // Hitch: 5-yard stop - CB drops to deep third zone (FS pace)
    routeTrigger: 'hitch',
    wrPositionTrigger: { x: 80, y: 70 },
    cbResponse: [
      { x: 80, y: 50 },
      { x: 80, y: 35 },
    ],
    responseDelay: 0,
  },
  {
    // Curl: 10-yard stop - CB matches WR, closes on the turn
    routeTrigger: 'curl',
    wrPositionTrigger: { x: 80, y: 55 },
    cbResponse: [
      { x: 80, y: 57 },
      { x: 78, y: 60 },
    ],
    responseDelay: 0,
  },
];

/**
 * DB/LB2 reaction rules for Cover 3 Match
 * PROXIMITY-BASED: DB/LB2 (at x=55) has a zone around him
 * - Match any WR that enters his zone (roughly x > 45)
 * - Release when WR crosses to LB's side (x < 45)
 * RB curl takes priority
 */
const cover3MatchDBLB2Reactions: DBLB2Reaction[] = [
  {
    // WR3 slant enters zone from right, DB/LB2 matches until WR crosses to LB side
    routeTrigger: 'slant',
    response: [
      { x: 55, y: 68 },  // read
      { x: 52, y: 60 },  // WR3 enters zone, match
      { x: 48, y: 52 },  // continue matching
      { x: 45, y: 48 },  // WR crosses to LB side, release
      { x: 50, y: 45 },  // settle back
      { x: 55, y: 42 },  // zone
    ],
  },
  {
    // WR4 slant enters zone from right
    routeTrigger: 'slant_wr4',
    response: [
      { x: 55, y: 68 },  // read
      { x: 50, y: 58 },  // WR4 enters zone, match
      { x: 46, y: 48 },  // continue matching
      { x: 45, y: 44 },  // WR crosses to LB side, release
      { x: 50, y: 42 },  // settle back
      { x: 55, y: 40 },  // zone
    ],
  },
  {
    // WR1 slant crosses into DB/LB2's zone from left (after LB releases)
    routeTrigger: 'slant_wr1_handoff',
    response: [
      { x: 55, y: 68 },  // read, wait
      { x: 55, y: 60 },  // backpedal
      { x: 55, y: 52 },  // WR1 enters zone
      { x: 60, y: 46 },  // pick up WR1
      { x: 66, y: 42 },  // trail
      { x: 73, y: 38 },  // continue
    ],
  },
  {
    // WR2 slant crosses into DB/LB2's zone from left
    routeTrigger: 'slant_wr2_handoff',
    response: [
      { x: 55, y: 68 },  // read, wait
      { x: 55, y: 58 },  // backpedal
      { x: 56, y: 48 },  // WR2 enters zone
      { x: 62, y: 44 },  // pick up WR2
      { x: 72, y: 42 },  // trail
      { x: 80, y: 40 },  // continue
    ],
  },
  {
    // WR3 drag enters zone from right - shallow cross, match then release at x=45
    routeTrigger: 'drag',
    response: [
      { x: 55, y: 70 },  // read
      { x: 52, y: 73 },  // WR3 drag enters zone, match
      { x: 48, y: 75 },  // continue matching
      { x: 45, y: 76 },  // WR crosses to LB side, release
      { x: 50, y: 73 },  // settle back
      { x: 55, y: 70 },  // zone
    ],
  },
  {
    // WR4 drag enters zone from right
    routeTrigger: 'drag_wr4',
    response: [
      { x: 55, y: 70 },  // read
      { x: 50, y: 73 },  // WR4 drag enters zone, match
      { x: 46, y: 74 },  // continue matching
      { x: 45, y: 75 },  // WR crosses to LB side, release
      { x: 50, y: 72 },  // settle back
      { x: 55, y: 70 },  // zone
    ],
  },
  {
    // WR1 drag crosses into DB/LB2's zone from left (after LB releases)
    routeTrigger: 'drag_wr1_handoff',
    response: [
      { x: 55, y: 70 },  // read, wait
      { x: 55, y: 72 },  // hold zone
      { x: 56, y: 74 },  // WR1 enters zone (x > 55)
      { x: 60, y: 75 },  // pick up WR1
      { x: 66, y: 75 },  // trail tight
      { x: 70, y: 75 },  // continue
    ],
  },
  {
    // WR2 drag crosses into DB/LB2's zone from left
    routeTrigger: 'drag_wr2_handoff',
    response: [
      { x: 55, y: 70 },  // read, wait
      { x: 55, y: 74 },  // hold zone
      { x: 58, y: 77 },  // WR2 enters zone (x > 55)
      { x: 65, y: 78 },  // pick up WR2
      { x: 74, y: 80 },  // trail tight
      { x: 80, y: 80 },  // continue
    ],
  },
  {
    // RB curl - PRIORITY
    routeTrigger: 'curl_rb',
    response: [
      { x: 55, y: 70 },
      { x: 54, y: 72 },
      { x: 52, y: 78 },
    ],
  },
  {
    // Zone drop
    routeTrigger: 'zone_drop',
    response: [
      { x: 55, y: 68 },
      { x: 55, y: 60 },
      { x: 55, y: 52 },
      { x: 55, y: 45 },
    ],
  },
  {
    // Out: No slant - zone drop (gradual backpedal)
    routeTrigger: 'out',
    response: [
      { x: 55, y: 70 },
      { x: 55, y: 67 },
      { x: 55, y: 62 },
      { x: 55, y: 55 },
      { x: 55, y: 50 },
    ],
  },
  {
    routeTrigger: 'out_wr4',
    response: [
      { x: 55, y: 70 },
      { x: 55, y: 67 },
      { x: 55, y: 62 },
      { x: 55, y: 55 },
      { x: 55, y: 50 },
    ],
  },
  {
    routeTrigger: 'out_rb',
    response: [
      { x: 55, y: 70 },
      { x: 55, y: 67 },
      { x: 55, y: 62 },
      { x: 55, y: 55 },
      { x: 55, y: 50 },
    ],
  },
  {
    routeTrigger: 'swing_rb',
    response: [
      { x: 55, y: 70 },
      { x: 55, y: 67 },
      { x: 55, y: 62 },
      { x: 55, y: 55 },
      { x: 55, y: 50 },
    ],
  },
  {
    routeTrigger: 'deepOut',
    response: [
      { x: 55, y: 70 },
      { x: 55, y: 67 },
      { x: 55, y: 62 },
      { x: 55, y: 55 },
      { x: 55, y: 50 },
    ],
  },
  {
    routeTrigger: 'deepIn',
    response: [
      { x: 55, y: 70 },
      { x: 55, y: 67 },
      { x: 55, y: 62 },
      { x: 55, y: 55 },
      { x: 55, y: 50 },
    ],
  },
  {
    routeTrigger: 'comeback',
    response: [
      { x: 55, y: 70 },
      { x: 55, y: 67 },
      { x: 55, y: 62 },
      { x: 55, y: 55 },
      { x: 55, y: 50 },
    ],
  },
  {
    routeTrigger: 'go',
    response: [
      { x: 55, y: 70 },
      { x: 55, y: 67 },
      { x: 55, y: 62 },
      { x: 55, y: 55 },
      { x: 55, y: 50 },
    ],
  },
  {
    routeTrigger: 'post',
    response: [
      { x: 55, y: 70 },
      { x: 55, y: 67 },
      { x: 55, y: 62 },
      { x: 55, y: 55 },
      { x: 55, y: 50 },
    ],
  },
  {
    routeTrigger: 'corner',
    response: [
      { x: 55, y: 70 },
      { x: 55, y: 67 },
      { x: 55, y: 62 },
      { x: 55, y: 55 },
      { x: 55, y: 50 },
    ],
  },
  {
    routeTrigger: 'drag',
    response: [
      { x: 55, y: 70 },
      { x: 55, y: 67 },
      { x: 55, y: 62 },
      { x: 55, y: 55 },
      { x: 55, y: 50 },
    ],
  },
  {
    routeTrigger: 'hitch',
    response: [
      { x: 55, y: 70 },
      { x: 55, y: 67 },
      { x: 55, y: 62 },
      { x: 55, y: 55 },
      { x: 55, y: 50 },
    ],
  },
  {
    routeTrigger: 'curl',
    response: [
      { x: 55, y: 70 },
      { x: 55, y: 67 },
      { x: 55, y: 62 },
      { x: 55, y: 55 },
      { x: 55, y: 50 },
    ],
  },
];

/**
 * SS reaction rules for Cover 3 Match
 * SS matches WR3 on vertical routes (go, post, corner, comeback, deepOut, deepIn) and short out
 * Lets WR3 go on 5yd or less inside routes (slant)
 * If WR3 goes inside and RB comes to right side, SS takes RB
 * Mirrored from DB/LB1
 */
const cover3MatchSSReactions: SSReaction[] = [
  {
    // Slant: 5yd inward route - let WR3 go, drop to zone
    routeTrigger: 'slant',
    response: [
      { x: 70, y: 65 },
      { x: 68, y: 55 },
      { x: 65, y: 45 },
    ],
  },
  {
    // Slant + RB out: WR3 goes inside, pick up RB on out route
    routeTrigger: 'slant_rb_out',
    response: [
      { x: 70, y: 68 },
      { x: 75, y: 75 },
      { x: 85, y: 77 },
    ],
  },
  {
    // Slant + RB swing: WR3 goes inside, pick up RB on swing route
    routeTrigger: 'slant_rb_swing',
    response: [
      { x: 70, y: 70 },
      { x: 80, y: 78 },
      { x: 90, y: 80 },
    ],
  },
  {
    // Slant + RB wheel: WR3 goes inside, pick up RB on wheel route
    routeTrigger: 'slant_rb_wheel',
    response: [
      { x: 70, y: 70 },
      { x: 80, y: 75 },
      { x: 85, y: 40 },
    ],
  },
  {
    // Out: Short 5-yard out - match WR3 to the flat
    routeTrigger: 'out',
    response: [
      { x: 72, y: 70 },
      { x: 80, y: 70 },
      { x: 90, y: 70 },
    ],
  },
  {
    // Deep Out: Vertical route - match WR3 downfield then to sideline
    routeTrigger: 'deepOut',
    response: [
      { x: 70, y: 60 },
      { x: 70, y: 47 },
      { x: 85, y: 45 },
    ],
  },
  {
    // Deep In: Vertical route - match WR3 downfield then across
    routeTrigger: 'deepIn',
    response: [
      { x: 70, y: 60 },
      { x: 70, y: 47 },
      { x: 45, y: 45 },
    ],
  },
  {
    // Comeback: Vertical route - match WR3 deep, close when he comes back
    routeTrigger: 'comeback',
    response: [
      { x: 70, y: 55 },
      { x: 70, y: 35 },
      { x: 72, y: 42 },
    ],
  },
  {
    // Go: Vertical route - match WR3 deep
    routeTrigger: 'go',
    response: [
      { x: 70, y: 55 },
      { x: 70, y: 35 },
      { x: 70, y: 15 },
    ],
  },
  {
    // Post: Vertical route - match WR3, break inside with him
    routeTrigger: 'post',
    response: [
      { x: 70, y: 60 },
      { x: 70, y: 50 },
      { x: 52, y: 20 },
    ],
  },
  {
    // Corner: Vertical route - match WR3, break outside with him
    routeTrigger: 'corner',
    response: [
      { x: 70, y: 60 },
      { x: 70, y: 50 },
      { x: 88, y: 20 },
    ],
  },
  {
    // Drag: Shallow cross - match WR3 across (mirrored)
    routeTrigger: 'drag',
    response: [
      { x: 70, y: 68 },
      { x: 65, y: 65 },
      { x: 30, y: 60 },
    ],
  },
  {
    // Hitch: 5-yard stop - match WR3 to the flat
    routeTrigger: 'hitch',
    response: [
      { x: 72, y: 70 },
      { x: 70, y: 73 },
      { x: 68, y: 76 },
    ],
  },
  {
    // Curl: 10-yard stop - match WR3, close on turn
    routeTrigger: 'curl',
    response: [
      { x: 70, y: 60 },
      { x: 70, y: 50 },
      { x: 68, y: 55 },
    ],
  },
];

/**
 * LB reaction rules for Cover 3 Match
 * PROXIMITY-BASED: LB (at x=45) has a zone around him
 * - Match any WR that enters his zone (roughly x < 55)
 * - Release when WR crosses to DB/LB2's side (x > 55)
 * RB curl goes to DB/LB2 (ends on right side)
 */
const cover3MatchLBReactions: LBReaction[] = [
  {
    // WR1 slant enters zone from left, LB matches until WR crosses to DB/LB2 side
    routeTrigger: 'slant_wr1',
    response: [
      { x: 45, y: 68 },  // read
      { x: 48, y: 60 },  // WR1 enters zone, match
      { x: 52, y: 52 },  // continue matching
      { x: 55, y: 48 },  // WR crosses to DB/LB2 side, release
      { x: 50, y: 45 },  // settle back
      { x: 45, y: 42 },  // zone
    ],
  },
  {
    // WR2 slant enters zone from left
    routeTrigger: 'slant_wr2',
    response: [
      { x: 45, y: 68 },  // read
      { x: 50, y: 58 },  // WR2 enters zone, match
      { x: 54, y: 48 },  // continue matching
      { x: 55, y: 44 },  // WR crosses to DB/LB2 side, release
      { x: 50, y: 42 },  // settle back
      { x: 45, y: 40 },  // zone
    ],
  },
  {
    // WR3 slant crosses into LB's zone from right (after DB/LB2 releases)
    routeTrigger: 'slant_wr3_handoff',
    response: [
      { x: 45, y: 68 },  // read, wait
      { x: 45, y: 60 },  // backpedal
      { x: 45, y: 52 },  // WR3 enters zone
      { x: 38, y: 48 },  // pick up WR3
      { x: 28, y: 45 },  // trail
      { x: 17, y: 43 },  // continue
    ],
  },
  {
    // WR4 slant crosses into LB's zone from right
    routeTrigger: 'slant_wr4_handoff',
    response: [
      { x: 45, y: 68 },  // read, wait
      { x: 45, y: 58 },  // backpedal
      { x: 44, y: 48 },  // WR4 enters zone
      { x: 38, y: 44 },  // pick up WR4
      { x: 30, y: 40 },  // trail
      { x: 25, y: 38 },  // continue
    ],
  },
  {
    // WR1 drag enters zone from left - match then release at x=55
    routeTrigger: 'drag_wr1',
    response: [
      { x: 45, y: 70 },  // read
      { x: 42, y: 74 },  // WR1 drag enters zone, match
      { x: 48, y: 75 },  // continue matching
      { x: 55, y: 75 },  // WR crosses to DB/LB2 side, release
      { x: 50, y: 72 },  // settle back
      { x: 45, y: 70 },  // zone
    ],
  },
  {
    // WR2 drag enters zone from left
    routeTrigger: 'drag_wr2',
    response: [
      { x: 45, y: 70 },  // read
      { x: 44, y: 76 },  // WR2 drag enters zone, match
      { x: 50, y: 78 },  // continue matching
      { x: 55, y: 78 },  // WR crosses to DB/LB2 side, release
      { x: 50, y: 74 },  // settle back
      { x: 45, y: 70 },  // zone
    ],
  },
  {
    // WR3 drag crosses into LB's zone from right (after DB/LB2 releases)
    routeTrigger: 'drag_wr3_handoff',
    response: [
      { x: 45, y: 70 },  // read, wait
      { x: 45, y: 72 },  // hold zone
      { x: 44, y: 74 },  // WR3 enters zone (x < 45)
      { x: 40, y: 76 },  // pick up WR3
      { x: 34, y: 78 },  // trail tight
      { x: 25, y: 80 },  // continue
    ],
  },
  {
    // WR4 drag crosses into LB's zone from right
    routeTrigger: 'drag_wr4_handoff',
    response: [
      { x: 45, y: 70 },  // read, wait
      { x: 45, y: 72 },  // hold zone
      { x: 42, y: 74 },  // WR4 enters zone (x < 45)
      { x: 36, y: 75 },  // pick up WR4
      { x: 30, y: 75 },  // trail tight
      { x: 25, y: 75 },  // continue
    ],
  },
  {
    // RB curl - goes to DB/LB2, LB zones
    routeTrigger: 'curl_rb',
    response: [
      { x: 45, y: 68 },
      { x: 45, y: 60 },
      { x: 45, y: 52 },
      { x: 45, y: 45 },
    ],
  },
  {
    // Zone drop
    routeTrigger: 'zone_drop',
    response: [
      { x: 45, y: 68 },
      { x: 45, y: 60 },
      { x: 45, y: 52 },
      { x: 45, y: 45 },
    ],
  },
];

/**
 * Complete Cover 3 Match coverage definition
 */
export const cover3MatchDefinition: CoverageDefinition = {
  id: 'cover3Match',
  name: 'Cover 3 Match',
  description: 'Cover 3 match coverage with deep zone responsibility',
  initialPosition: CB1_COVER3MATCH_START,
  reactionRules: cover3MatchReactions,
  dblb1InitialPosition: DBLB1_COVER3MATCH_START,
  dblb1ReactionRules: cover3MatchDBLB1Reactions,
  fsInitialPosition: FS_COVER3MATCH_START,
  fsReactionRules: cover3MatchFSReactions,
  cb2InitialPosition: CB2_COVER3MATCH_START,
  cb2ReactionRules: cover3MatchCB2Reactions,
  dblb2InitialPosition: DBLB2_COVER3MATCH_START,
  dblb2ReactionRules: cover3MatchDBLB2Reactions,
  ssInitialPosition: SS_COVER3MATCH_START,
  ssReactionRules: cover3MatchSSReactions,
  lbInitialPosition: LB_COVER3MATCH_START,
  lbReactionRules: cover3MatchLBReactions,
};

export function getCover3MatchStartPosition(): Position {
  return { ...CB1_COVER3MATCH_START };
}

export function getCover3MatchDBLB1StartPosition(): Position {
  return { ...DBLB1_COVER3MATCH_START };
}

export function getCover3MatchFSStartPosition(): Position {
  return { ...FS_COVER3MATCH_START };
}

export function getCover3MatchReaction(routeTrigger: string): CoverageReaction | undefined {
  return cover3MatchReactions.find((r) => r.routeTrigger === routeTrigger);
}

export function getCover3MatchDBLB1Reaction(routeTrigger: string): DBLBReaction | undefined {
  return cover3MatchDBLB1Reactions.find((r) => r.routeTrigger === routeTrigger);
}

export function getCover3MatchFSReaction(routeTrigger: string): FSReaction | undefined {
  return cover3MatchFSReactions.find((r) => r.routeTrigger === routeTrigger);
}

export function getCover3MatchReactions(): CoverageReaction[] {
  return [...cover3MatchReactions];
}

export function getCover3MatchDefinition(): CoverageDefinition {
  return { ...cover3MatchDefinition };
}

export {
  CB1_COVER3MATCH_START,
  DBLB1_COVER3MATCH_START,
  FS_COVER3MATCH_START,
  CB2_COVER3MATCH_START,
  DBLB2_COVER3MATCH_START,
  SS_COVER3MATCH_START,
  LB_COVER3MATCH_START,
  cover3MatchReactions,
  cover3MatchDBLB1Reactions,
  cover3MatchFSReactions,
  cover3MatchCB2Reactions,
  cover3MatchDBLB2Reactions,
  cover3MatchSSReactions,
  cover3MatchLBReactions,
};
